import api from './api';

// Image upload service
export const imageAPI = {
  // Upload multiple images (admin only)
  uploadImages: async (files, folder = 'admin') => {
    const formData = new FormData();
    
    // Add files to FormData
    files.forEach((file, index) => {
      formData.append('images', file);
    });
    
    // Add folder parameter
    formData.append('folder', folder);
    
    // Do not set Content-Type — browser/axios must add multipart boundary
    const response = await api.post('/api/images/upload', formData);
    
    return response.data;
  },

  /**
   * Upload one Paw Stories reel (or similar) video. Admin or vendor.
   * @param {File} file
   * @param {string} folder S3 folder segment (default paw-stories)
   */
  uploadPawStoryVideo: async (file, folder = 'paw-stories') => {
    const formData = new FormData();
    formData.append('video', file);
    formData.append('folder', folder);
    const response = await api.post('/api/images/upload-video', formData, {
      timeout: 15 * 60 * 1000 // server transcodes large files; can take several minutes
    });
    return response.data;
  },

  // Upload multiple images (public - for regular users)
  uploadImagesPublic: async (files, folder = 'public') => {
    const formData = new FormData();
    
    // Add files to FormData
    files.forEach((file, index) => {
      formData.append('images', file);
    });
    
    // Add folder parameter
    formData.append('folder', folder);
    
    const response = await api.post('/api/images/upload-public', formData);
    
    return response.data;
  },

  // Delete image
  deleteImage: async (url) => {
    const response = await api.delete('/api/images/delete', {
      data: { url }
    });
    
    return response.data;
  },

  // Get upload configuration
  getConfig: async () => {
    const response = await api.get('/api/images/config');
    return response.data;
  },

  /** Upload mixed gallery photos + videos (videos use paw-story transcode pipeline). */
  uploadGalleryMedia: async (files, folder = 'stays') => {
    const imageFiles = [];
    const videoFiles = [];
    const errors = [];

    files.forEach((file) => {
      if (imageUtils.isValidImageType(file)) {
        if (!imageUtils.isValidFileSize(file)) {
          errors.push(`${file.name}: Image too large (max 10MB).`);
        } else {
          imageFiles.push(file);
        }
        return;
      }
      if (imageUtils.isValidPawStoryVideoType(file)) {
        if (!imageUtils.isValidPawStoryVideoSize(file)) {
          errors.push(`${file.name}: Video too large (max 500MB raw upload).`);
        } else {
          videoFiles.push(file);
        }
        return;
      }
      errors.push(`${file.name}: Unsupported type. Use JPEG, PNG, WebP, GIF, MP4, MOV, or WebM.`);
    });

    const urls = [];

    if (imageFiles.length > 0) {
      const response = await imageAPI.uploadImages(imageFiles, folder);
      if (response.uploaded?.length) {
        urls.push(...response.uploaded.map((item) => item.url));
      }
      if (response.errors?.length) {
        response.errors.forEach((e) => {
          errors.push(`${e.originalName || 'Image'}: ${e.error || 'Upload failed'}`);
        });
      }
    }

    for (const file of videoFiles) {
      try {
        const response = await imageAPI.uploadPawStoryVideo(file, folder);
        const url = response.uploaded?.[0]?.url;
        if (url) urls.push(url);
        else errors.push(`${file.name}: Video upload returned no URL.`);
      } catch (err) {
        const d = err.response?.data?.details || err.response?.data?.error || err.message;
        errors.push(`${file.name}: ${d || 'Video upload failed'}`);
      }
    }

    return { urls, errors };
  }
};

// Utility functions for image handling
export const imageUtils = {
  // Validate file type
  isValidImageType: (file) => {
    const validTypes = [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/gif',
      'image/heic',
      'image/heif',
      'image/avif'
    ];
    return validTypes.includes((file.type || '').toLowerCase());
  },

  isValidPawStoryVideoType: (file) => {
    const valid = new Set(['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v', 'video/m4v']);
    return valid.has((file.type || '').toLowerCase());
  },

  /** Max raw upload to match server multer (delivered file is transcoded to ≤10MB). */
  isValidPawStoryVideoSize: (file, maxBytes = 500 * 1024 * 1024) => {
    return file.size <= maxBytes;
  },

  // Validate file size (10MB limit)
  isValidFileSize: (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    return file.size <= maxSize;
  },

  // Format file size for display
  formatFileSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // Create preview URL for image
  createPreviewUrl: (file) => {
    return URL.createObjectURL(file);
  },

  // Revoke preview URL to free memory
  revokePreviewUrl: (url) => {
    URL.revokeObjectURL(url);
  },

  // Get file extension
  getFileExtension: (filename) => {
    return filename.split('.').pop().toLowerCase();
  },

  // Generate unique filename
  generateUniqueFilename: (originalName) => {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = originalName.split('.').pop();
    return `${timestamp}_${randomString}.${extension}`;
  },

  galleryVideoAccept:
    'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,image/avif,video/mp4,video/quicktime,video/webm,video/x-m4v,video/m4v,.mp4,.mov,.webm,.m4v'
};
